/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow
import type { Element } from 'react';

import React from 'react';
import { Col, Form, FormText, Row, Table } from 'reactstrap';
import {
  COST_PER_SQ_FT,
  FIRST_STORY_FULL_WALL_RATIO_DIR_1,
  FIRST_STORY_FULL_WALL_RATIO_DIR_2,
  FIRST_STORY_HEIGHT,
  FIRST_STORY_INFILLED_WALL_RATIO_DIR_1,
  FIRST_STORY_INFILLED_WALL_RATIO_DIR_2,
  HAZARD_EDITION,
  REPAIR_COSTS_SCALE_WITH_BUILDING_VALUE,
  SITE_CLASS,
  TDMF_BAY_LENGTH_DIR_1,
  TDMF_BAY_LENGTH_DIR_2,
  TDMF_DAMPER_CONFIG_DIR_1,
  TDMF_DAMPER_CONFIG_DIR_2,
  TDMF_DAMPER_EXPOSURE_DIR_1,
  TDMF_DAMPER_EXPOSURE_DIR_2,
  UPPER_STORY_FULL_WALL_RATIO_DIR_1,
  UPPER_STORY_FULL_WALL_RATIO_DIR_2,
  UPPER_STORY_HEIGHT,
  UPPER_STORY_INFILLED_WALL_RATIO_DIR_1,
  UPPER_STORY_INFILLED_WALL_RATIO_DIR_2,
  YLBF_BAY_LENGTH_DIR_1,
  YLBF_BAY_LENGTH_DIR_2,
  YLBF_BRACE_CONFIG_DIR_1,
  YLBF_BRACE_CONFIG_DIR_2,
  YLBF_BRACE_EXPOSURE_DIR_1,
  YLBF_BRACE_EXPOSURE_DIR_2,
  YLBF_NUM_FUSES_PER_BRACE_DIR_1,
  YLBF_NUM_FUSES_PER_BRACE_DIR_2,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import { BUILDING_LAYOUT_SECTION } from '@hbrisk/sp3-risk-model-support/models/modelPage/sectionIds.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import { Field, Fieldset, FormGroup } from '#components/pages/Models/Model/support/form/index.js';
import { modelTypesByForm } from '#src/js/constants/models/modelTypes/index.js';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import { directionCol } from './BuildingLayoutSection.module.scss';
import FloorPlanFieldSet from './FloorPlanFieldset/index.js';

type Props = {|
  form: string,
  heading: string,
  onBack: Function,
  onNext: Function,
  showRepairCostsScaleWithBuildingValue: boolean,
|};

const BuildingLayoutSection = (props: Props): Element<'div'> => {
  const { heading, form, onBack, onNext, showRepairCostsScaleWithBuildingValue } = props;

  const { fieldsByName, isFieldInSection } = modelTypesByForm[form];
  const showSoilInfo = isFieldInSection(SITE_CLASS, BUILDING_LAYOUT_SECTION);
  const showHazardEdition = isFieldInSection(HAZARD_EDITION, BUILDING_LAYOUT_SECTION);

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate>
        <Fieldset>
          <legend>Building Replacement Value</legend>
          <Row>
            <Col lg="6">
              <Field field={fieldsByName[COST_PER_SQ_FT]} />
            </Col>
          </Row>
          {showRepairCostsScaleWithBuildingValue ? (
            <Field field={fieldsByName[REPAIR_COSTS_SCALE_WITH_BUILDING_VALUE]} />
          ) : (
            <FormText color="muted">*Component repair costs scale with building value</FormText>
          )}
        </Fieldset>
        <FloorPlanFieldSet form={form} />
        <Fieldset>
          <legend>Story Heights</legend>
          <Row>
            <Col lg="6">
              <Field field={fieldsByName[FIRST_STORY_HEIGHT]} />
            </Col>
          </Row>
          <Row>
            <Col lg="6">
              <Field field={fieldsByName[UPPER_STORY_HEIGHT]} />
            </Col>
          </Row>
        </Fieldset>
        <FormGroup
          form={form}
          fields={[
            FIRST_STORY_FULL_WALL_RATIO_DIR_1,
            FIRST_STORY_FULL_WALL_RATIO_DIR_2,
            UPPER_STORY_FULL_WALL_RATIO_DIR_1,
            UPPER_STORY_FULL_WALL_RATIO_DIR_2,
          ]}
        >
          <Fieldset>
            <legend>Wood Light Frame Properties</legend>
            <Row>
              <Col lg="6">
                <strong>Full Height Exterior Wall (Fraction)</strong>{' '}
                <Tip tipId="fullHeightExteriorWallFraction" />
              </Col>
            </Row>
            <Table responsive className="structural-properties">
              <thead>
                <tr>
                  <th />
                  <th className={directionCol}>Direction 1</th>
                  <th className={directionCol}>Direction 2</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-nowrap">First Story</td>
                  <td>
                    <Field field={fieldsByName[FIRST_STORY_FULL_WALL_RATIO_DIR_1]} excludeLabel />
                  </td>
                  <td>
                    <Field field={fieldsByName[FIRST_STORY_FULL_WALL_RATIO_DIR_2]} excludeLabel />
                  </td>
                </tr>
                <tr>
                  <td className="text-nowrap">Upper Stories</td>
                  <td>
                    <Field field={fieldsByName[UPPER_STORY_FULL_WALL_RATIO_DIR_1]} excludeLabel />
                  </td>
                  <td>
                    <Field field={fieldsByName[UPPER_STORY_FULL_WALL_RATIO_DIR_2]} excludeLabel />
                  </td>
                </tr>
              </tbody>
            </Table>
          </Fieldset>
        </FormGroup>
        <FormGroup
          form={form}
          fields={[
            FIRST_STORY_INFILLED_WALL_RATIO_DIR_1,
            FIRST_STORY_INFILLED_WALL_RATIO_DIR_2,
            UPPER_STORY_INFILLED_WALL_RATIO_DIR_1,
            UPPER_STORY_INFILLED_WALL_RATIO_DIR_2,
          ]}
        >
          <Fieldset>
            <legend>Masonry Infill Properties</legend>
            <Row>
              <Col lg="6">
                <strong>Infilled Exterior Wall (Fraction)</strong>{' '}
                <Tip tipId="infilledExteriorWallFraction" />
              </Col>
            </Row>
            <Table responsive>
              <thead>
                <tr>
                  <th />
                  <th className={directionCol}>Direction 1</th>
                  <th className={directionCol}>Direction 2</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-nowrap">First Story</td>
                  <td>
                    <Field
                      field={fieldsByName[FIRST_STORY_INFILLED_WALL_RATIO_DIR_1]}
                      excludeLabel
                    />
                  </td>
                  <td>
                    <Field
                      field={fieldsByName[FIRST_STORY_INFILLED_WALL_RATIO_DIR_2]}
                      excludeLabel
                    />
                  </td>
                </tr>
                <tr>
                  <td className="text-nowrap">Upper Stories</td>
                  <td>
                    <Field
                      field={fieldsByName[UPPER_STORY_INFILLED_WALL_RATIO_DIR_1]}
                      excludeLabel
                    />
                  </td>
                  <td>
                    <Field
                      field={fieldsByName[UPPER_STORY_INFILLED_WALL_RATIO_DIR_2]}
                      excludeLabel
                    />
                  </td>
                </tr>
              </tbody>
            </Table>
          </Fieldset>
        </FormGroup>
        <FormGroup
          form={form}
          fields={[
            TDMF_DAMPER_CONFIG_DIR_1,
            TDMF_BAY_LENGTH_DIR_1,
            TDMF_DAMPER_EXPOSURE_DIR_1,
            TDMF_DAMPER_CONFIG_DIR_2,
            TDMF_BAY_LENGTH_DIR_2,
            TDMF_DAMPER_EXPOSURE_DIR_2,
          ]}
        >
          <Fieldset>
            <legend>Taylor Damped Moment Frame Properties</legend>
            <Table responsive>
              <thead>
                <tr>
                  <th />
                  <th className={directionCol}>Direction 1</th>
                  <th className={directionCol}>Direction 2</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-nowrap">
                    Damper Brace Configuration <Tip tipId="tdmfBraceConfig" />
                  </td>
                  <td>
                    <Field field={fieldsByName[TDMF_DAMPER_CONFIG_DIR_1]} excludeLabel />
                  </td>
                  <td>
                    <Field field={fieldsByName[TDMF_DAMPER_CONFIG_DIR_2]} excludeLabel />
                  </td>
                </tr>
                <tr>
                  <td className="text-nowrap">
                    Damper Bay Length <Tip tipId="tdmfBayLength" />
                  </td>
                  <td>
                    <Field field={fieldsByName[TDMF_BAY_LENGTH_DIR_1]} excludeLabel />
                  </td>
                  <td>
                    <Field field={fieldsByName[TDMF_BAY_LENGTH_DIR_2]} excludeLabel />
                  </td>
                </tr>
                <tr>
                  <td className="text-nowrap">
                    Damper Exposure <Tip tipId="tdmfDamperExposure" />
                  </td>
                  <td>
                    <Field field={fieldsByName[TDMF_DAMPER_EXPOSURE_DIR_1]} excludeLabel />
                  </td>
                  <td>
                    <Field field={fieldsByName[TDMF_DAMPER_EXPOSURE_DIR_2]} excludeLabel />
                  </td>
                </tr>
              </tbody>
            </Table>
          </Fieldset>
        </FormGroup>
        <FormGroup
          form={form}
          fields={[
            YLBF_BRACE_CONFIG_DIR_1,
            YLBF_BRACE_CONFIG_DIR_2,
            YLBF_NUM_FUSES_PER_BRACE_DIR_1,
            YLBF_NUM_FUSES_PER_BRACE_DIR_2,
            YLBF_BAY_LENGTH_DIR_1,
            YLBF_BAY_LENGTH_DIR_2,
            YLBF_BRACE_EXPOSURE_DIR_1,
            YLBF_BRACE_EXPOSURE_DIR_2,
          ]}
        >
          <Fieldset>
            <legend>Simpson Strong-Tie Yield-Link Braced Frame Properties</legend>
            <Table responsive>
              <thead>
                <tr>
                  <th />
                  <th className={directionCol}>Direction 1</th>
                  <th className={directionCol}>Direction 2</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-nowrap">
                    Brace Configuration <Tip tipId="ylbfBraceConfig" />
                  </td>
                  <td>
                    <Field field={fieldsByName[YLBF_BRACE_CONFIG_DIR_1]} excludeLabel />
                  </td>
                  <td>
                    <Field field={fieldsByName[YLBF_BRACE_CONFIG_DIR_2]} excludeLabel />
                  </td>
                </tr>
                <tr>
                  <td className="text-nowrap">
                    Yield-Links per Brace <Tip tipId="ylbfNumFusesPerBrace" />
                  </td>
                  <td>
                    <Field field={fieldsByName[YLBF_NUM_FUSES_PER_BRACE_DIR_1]} excludeLabel />
                  </td>
                  <td>
                    <Field field={fieldsByName[YLBF_NUM_FUSES_PER_BRACE_DIR_2]} excludeLabel />
                  </td>
                </tr>
                <tr>
                  <td className="text-nowrap">
                    Bay Length <Tip tipId="ylbfBayLength" />
                  </td>
                  <td>
                    <Field field={fieldsByName[YLBF_BAY_LENGTH_DIR_1]} excludeLabel />
                  </td>
                  <td>
                    <Field field={fieldsByName[YLBF_BAY_LENGTH_DIR_2]} excludeLabel />
                  </td>
                </tr>
                <tr>
                  <td className="text-nowrap">
                    Brace Exposure <Tip tipId="ylbfBraceExposure" />
                  </td>
                  <td>
                    <Field field={fieldsByName[YLBF_BRACE_EXPOSURE_DIR_1]} excludeLabel />
                  </td>
                  <td>
                    <Field field={fieldsByName[YLBF_BRACE_EXPOSURE_DIR_2]} excludeLabel />
                  </td>
                </tr>
              </tbody>
            </Table>
          </Fieldset>
        </FormGroup>
        {showSoilInfo && (
          <Fieldset>
            <legend>Soil Information</legend>
            <Field field={fieldsByName[SITE_CLASS]} />
          </Fieldset>
        )}
        {showHazardEdition && (
          <Fieldset>
            <legend>Hazard Edition</legend>
            <Field field={fieldsByName[HAZARD_EDITION]} />
          </Fieldset>
        )}
        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default BuildingLayoutSection;
